import { AfterViewInit, Input, Directive } from "@angular/core";
import { Fiat, FiatWallet, Offer } from "@models";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TransactionListCoreComponent } from "../transaction-list-core.component";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Tradable } from "@models/interfaces/tradable";
import { IndexWallet } from "@models/asset/index/index_wallet";
import { convertTradableToFiat, toEuroByFiatId } from "@helpers/tradable-convert";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { TooltipCoinOptions, TooltipService, TooltipTradableAction } from "@main-services/tooltip.service";
import { FiatService } from "@main-services/currency";
import { ContactService } from "@main-services/contact.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { UserService } from "@main-services/user.service";
import { ModalService } from "@core-services/modal.service";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { TradeSellComponent } from "@modules/trade/modals/trade-sell-modal/trade-sell.component";
import { TradeSwapComponent } from "@modules/trade/modals/trade-swap-modal/trade-swap.component";
import { TradeBuyComponent } from "@modules/trade/modals/trade-buy-modal/trade-buy-modal.component";
import { TOKEN_BEST_ID } from "@helpers/constants";
import { Best2TermsModalComponent } from "@modules/trade/modals/best-2-terms-modal/components/best-2-terms-modal.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";
import { FiatDepositEntryService } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit-entry.service";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { take } from "rxjs/operators";
import { isEarnOnFiatTagComparisonHelper } from "@modules/transaction/transaction.utils";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";

@UntilDestroy()
@Directive()
export abstract class TransactionListFooterComponent extends TransactionListCoreComponent implements AfterViewInit {
	@Input()
	public PID: string = "";
	protected fiatWalletsAboveMinBuy: FiatWallet[] = [];
	protected transactionFiat: Fiat = null;
	protected footerTradable: Tradable = null;
	protected abstract translationService: TranslationService;

	protected constructor(
		protected modalService: ModalService,
		protected tradableWalletListService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected fiatWalletService: FiatWalletService,
		protected fiatService: FiatService,
		protected tooltipService: TooltipService,
		protected userService: UserService,
		protected contactService: ContactService,
		protected payaccountService: PayAccountService,
		protected infoService: InfoService,
		protected userFiatFundsService: UserFiatFundsService,
		protected readonly fiatDepositEntryService: FiatDepositEntryService,
		protected readonly launchDarklyService: LaunchDarklyService,
		protected readonly checkoutEntryHelper: CheckoutEntryHelperService
	) {
		super(
			modalService,
			tradableWalletListService,
			tradableListService,
			fiatWalletService,
			fiatService,
			contactService,
			payaccountService
		);
	}

	public get buttonLabel(): string {
		if (this.getTradeType().length > 0) {
			return this.getTradeType() + "_more_list_item_label";
		}
		return "";
	}

	public get checkAllTradableWallets(): boolean {
		return this.transaction.getType() === "sell";
	}

	public abstract showFooterButtonWithdrawOrTradable(): boolean;
	public abstract showFooterButtonNOTWithdrawOrTradable(): boolean;

	public getTradeType(): string {
		if (this.transaction.hasTrade() && this.transaction.getTrade().getIsSwap()) {
			return "swap";
		}
		if (this.transaction.isSavings || (this.transaction.hasTrade() && this.transaction.getTrade().getIsSavings())) {
			return "";
		}
		return this.transaction.getType();
	}

	public ngAfterViewInit(): void {
		this.tradableListService
			.getList()
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.updateFiatWalletsAboveMinBuy();
			});

		this.fiatWalletService
			.getList()
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.updateFiatWalletsAboveMinBuy();
			});

		super.ngAfterViewInit();
	}

	public getTransactionFiat(): Fiat {
		if (!this.transactionFiat) {
			this.setTransactionFiat();
		}
		return this.transactionFiat;
	}

	public setTransactionFiat(): void {
		this.transactionFiat = this.fiat;
	}

	// TODO: Remove after 01.01.2022
	public handleBest2Terms(useCase: string = "buy"): void {
		const best2TermsModal = this.modalService.create(Best2TermsModalComponent, null, { showBackdrop: false });
		best2TermsModal.present();
		best2TermsModal.onDismiss.subscribe((res) => {
			if (res.data === "accepted") {
				this.tradeMore(useCase);
			}
		});
	}

	public tradeMore(useCase: string = "buy"): void {
		if (this.disableActionButton(useCase)) {
			return;
		}

		this.launchDarklyService
			.isActive$(LD_FLAG.CHECKOUT_FLOW_ENABLED)
			.pipe(take(1))
			.subscribe({
				next: (flag) => {
					const canProceedToBuy = flag || this.userFiatFundsService.hasFiatWalletsWithFunds();
					if (
						(this._tradableWallet?.getCurrencyId() === TOKEN_BEST_ID ||
							this.tradable?.getId() === TOKEN_BEST_ID) &&
						["buy", "savings", "deposit"].some((a) => a === this.transaction.getType()) &&
						this.accountService.showBest2TermsOnBestTrade()
					) {
						this.handleBest2Terms(useCase);
						return;
					}

					if (useCase === "buy" && !canProceedToBuy) {
						this.fiatDepositEntryService.openDepositNow();
					}

					this.modalService.popAll();
					setTimeout(() => {
						if ((useCase === "buy" && canProceedToBuy) || useCase === "sell" || useCase === "swap") {
							const offer = this.createOffer();
							const tradableWallet: TradableWallet = this.getTradableWalletWithEnoughFunds();
							offer.setWalletId(tradableWallet.getId());
							offer.setCurrencyId(tradableWallet.getCurrencyId());
							offer.setIndexTrade(tradableWallet instanceof IndexWallet);
							if (tradableWallet.getId() !== this.tradableWallet.getId()) {
								this.infoService.setInfo(
									this.translationService.translate("more_transaction_wallet_switch", [
										this.tradableWallet.getName(),
										tradableWallet.getName()
									])
								);
							}

							// BUY starts here
							if (useCase === "buy") {
								if (flag) {
									// New CHECKOUT BUY flow
									this.modalService.pop();
									this.checkoutEntryHelper.onTradableSelect(this.tradable?.getId());
								} else {
									// Legacy BUY flow
									offer.setType(TRADE_TYPE.BUY);
									this.modalService.create(TradeBuyComponent, { data: offer }).present();
								}
							} else if (useCase === "sell") {
								offer.setType(TRADE_TYPE.SELL);
								this.modalService.create(TradeSellComponent, { data: offer }).present();
							} else if (useCase === "swap") {
								this.modalService
									.create(TradeSwapComponent, {
										externalLaunchOptions: { wallet: tradableWallet }
									})
									.present();
							}
						}
					}, 100); // Timeout because otherwise index footer closes the buy/sell flow. Can be removed after modal-rework and replaced with a promise
				}
			});
	}

	public getTxFooterTradable(): Tradable {
		if (!this.footerTradable) {
			this.setTxFooterTradable();
		}
		return this.footerTradable;
	}

	public setTxFooterTradable(): void {
		this.footerTradable =
			this.tradableWallet instanceof IndexWallet
				? this.tradableListService.getItemById(this.tradableWallet.getCurrencyId())
				: this.tradable;
	}

	public createOffer(): Offer {
		const offer = new Offer();

		if (this.fiatWallet) {
			offer.setFiatWalletId(this.fiatWallet.getId());
			offer.setFiatId(this.fiatWallet.getFiatId());
		}

		offer.setDirectTradableWalletOffer(true);
		return offer;
	}

	public getTradableWalletWithEnoughFunds(): TradableWallet {
		let wallet: TradableWallet = this.tradableWallet;
		const tradable: Tradable = this.tradableListService.getItemById(wallet.getCurrencyId());
		const walletsAboveMinSell: TradableWallet[] = this.tradableWalletListService
			.getNonEmptyWallets(tradable)
			.filter((cryptoWallet) => {
				const cryptoToFiatAmount = convertTradableToFiat(
					tradable,
					cryptoWallet.getBalance(),
					this.accountService.getDefaultFiat()
				);
				return cryptoToFiatAmount >= tradable.getMinSell();
			});
		// Checks if current tradableWallet is above minSell
		// If not, finds the most valuable wallet with the same tradable
		if (this.checkAllTradableWallets && !walletsAboveMinSell.some((w) => w.getId() === wallet.getId())) {
			wallet = this.tradableWalletListService
				.getWalletsByTradableId(tradable.getId())
				.reduce((prev: TradableWallet, curr: TradableWallet) =>
					curr.getBalance() > prev.getBalance() || !prev ? curr : prev
				);
		}
		return wallet;
	}

	public disableActionButton(s: string): boolean {
		if (this.userService.isVerificationMandatory) {
			return true;
		}
		if (this.tradable) {
			const lessThanMinSell: boolean =
				this.tradableWallet instanceof IndexWallet
					? this.tradableWallet.getBalance() < this.getTxFooterTradable().getMinSell()
					: this.lessThanMinSell();

			if (s === "buy") {
				return !this.getTxFooterTradable().isBuyActive() || !this.getTxFooterTradable().isAvailable();
			}
			if (s === "sell") {
				return (
					lessThanMinSell ||
					!this.getTxFooterTradable().isSellActive() ||
					!this.getTxFooterTradable().isAvailable()
				);
			}
			if (s === "swap") {
				return (
					this.accountService.account.getSwapDisabled() ||
					lessThanMinSell ||
					!this.getTxFooterTradable().isSellActive() ||
					!this.getTxFooterTradable().isAvailable()
				);
			}
		}
		if (s === "withdrawal") {
			return this.fiatWallet.getBalance() < +this.fiat.getMinWalletWithdraw();
		}
		return false;
	}

	public checkLessThanMinSell(wallet: TradableWallet): boolean {
		return convertTradableToFiat(this.tradable, wallet?.getBalance(), this.fiat) < this.tradable.getMinSell();
	}

	public lessThanMinSell(): boolean {
		return this.checkAllTradableWallets
			? this.tradableWalletListService
					.getNonEmptyWallets(this.tradable)
					.every((t) => this.checkLessThanMinSell(t))
			: this.checkLessThanMinSell(this.tradableWallet);
	}

	public showButton(): boolean {
		return (
			this.getTradeType().length > 0 &&
			this.getTradeType() !== "internal_transfer" &&
			!this.isBestFeeTx() &&
			!this.isItbOrRewardTx() &&
			!this.isEarnOnFiatAsset() &&
			!this.isLeveragedToken() &&
			!this.isBestToken()
		);
	}

	public amountForButton(action: string): string {
		const transactionAction: string = action === "transfer" ? "send" : action;
		const tooltipOptions: TooltipCoinOptions = {
			action: transactionAction as TooltipTradableAction,
			tradable: this.getTxFooterTradable(),
			fiat: this.fiat
		};
		return this.tooltipService.getAmountForTooltip(tooltipOptions);
	}

	public getToolTip(action: string): string {
		const transactionAction: string = action === "transfer" ? "send" : action;
		const tooltipOptions: TooltipCoinOptions = {
			action: transactionAction as TooltipTradableAction,
			tradable: this.getTxFooterTradable(),
			fiat: this.fiat,
			tradableWallet: this.tradableWallet,
			checkAllTradableWallets: this.tradableWallet instanceof IndexWallet ? false : this.checkAllTradableWallets
		};
		return this.tooltipService.getTooltip(tooltipOptions);
	}

	private updateFiatWalletsAboveMinBuy(): void {
		if (!this.tradable) {
			return;
		}
		if (this.fiatWallet) {
			this.fiatWalletsAboveMinBuy = [];
			if (
				toEuroByFiatId(
					this.fiatWallet.getBalance(),
					this.fiatService.getItemById(this.fiatWallet.getFiatId())
				) >= this.getTxFooterTradable().getMinBuy()
			) {
				this.fiatWalletsAboveMinBuy.push(this.fiatWallet);
			}
		}
	}

	public isItbOrRewardTx(): boolean {
		return (
			this.transaction.getTagShortNames().some((t) => t === "instant_trade_bonus") ||
			this.transaction.getTagShortNames().some((t) => t === "reward")
		);
	}

	// check for tag name (MMF)
	private isEarnOnFiatAsset(): boolean {
		const trade = this.transaction?.getTrade();
		return isEarnOnFiatTagComparisonHelper(trade?.getTags());
	}

	public isLeveragedToken(): boolean {
		return (
			this.getTradeType() === "buy" &&
			this.tradableListService.getItemById(this.tradableWallet.getCurrencyId()) instanceof LeveragedToken
		);
	}

	public isBestToken(): boolean {
		return this.getTradeType() === "buy" && this.tradableWallet.getCurrencyId() === TOKEN_BEST_ID;
	}
}
